// Single source of truth for the API origin. HTTPS only, no cleartext
// fallback, no localhost default baked in -- this is the exact thing
// ops/security-audit-p0.md (findings #4/#7) and
// ops/dashboard-client-audit.md flagged the legacy dashboard for getting
// wrong. There is deliberately no environment-variable override that could
// point this at an arbitrary/attacker-controlled host at runtime; changing
// the target API is a source-code change and a new build, not a runtime
// config knob.
export const API_BASE_URL = "https://api.bitclock.net";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  /** Parsed JSON error body, when there was one. Some errors carry more
   * than a `detail` string (e.g. the device-limit 409's
   * {"error","message","upsell"} shape) -- callers that need those fields
   * read them off here (see api/auth.ts's deviceLimitInfo). */
  readonly body: unknown;

  constructor(status: number, detail: string, body: unknown = null) {
    super(`API error ${status}: ${detail}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  auth?: boolean; // attach Authorization: Bearer <token> if a token is stored
  signal?: AbortSignal;
  keepalive?: boolean; // let a fire-and-forget POST outlive a page navigation
}

let getToken: () => string | null = () => null;

/** Wired up by api/auth.ts to avoid a circular import; client.ts stays the
 * one place that knows how to *use* a token, auth.ts stays the one place
 * that knows how tokens are stored. */
export function _setTokenGetter(fn: () => string | null): void {
  getToken = fn;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = false, signal, keepalive } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // credentials deliberately omitted (defaults to "same-origin", and this
  // is always cross-origin to the API so no cookie is ever sent) -- the
  // API's own CORS config never pairs credentialed requests with any
  // origin (allow_credentials=False, always) and auth here is bearer-token
  // only, so there is nothing for a cookie jar to do.
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    keepalive,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  let json: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      json = JSON.parse(text);
    } catch {
      // Non-JSON body (shouldn't happen for this API) -- fall through and
      // let the ok/!ok branch below decide what to do with an empty detail.
    }
  }

  if (!response.ok) {
    const detail =
      json && typeof json === "object" && "detail" in json
        ? String((json as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, detail, json);
  }

  return json as T;
}
